import dayjs from "dayjs";

export const CATEGORIES = [
  "AUTH",
  "SECURITY",
  "COMPANY",
  "TAX_COMPUTATION",
  "INTEGRATION",
  "SUBSCRIPTION",
  "SYSTEM",
];
export const FILTER_LABELS = {
  actor_email: "Actor email",
  action: "Action",
  category: "Category",
  status: "Status",
  actor_id: "Actor ID",
  target_id: "Target ID",
  target_type: "Target type",
  request_id: "Request ID",
  ip_address: "IP address",
};
export const TIME_PRESETS = {
  "24h": "Last 24 hours",
  today: "Today",
  "7d": "Last 7 days",
  custom: "Custom range",
  all: "All time",
};
export const localTimezone =
  Intl.DateTimeFormat().resolvedOptions().timeZone || "Local time";
export const defaultFilters = () => ({
  page: 1,
  limit: 20,
  ...Object.fromEntries(Object.keys(FILTER_LABELS).map((key) => [key, ""])),
  period: "24h",
  from: "",
  to: "",
});
export const humanize = (value) =>
  String(value || "Unknown")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
export const formatTime = (value, format = "DD MMM YYYY, HH:mm:ss") =>
  value && dayjs(value).isValid() ? dayjs(value).format(format) : "—";
export const displayValue = (value) =>
  value === undefined
    ? "Not recorded"
    : value === null
      ? "null"
      : typeof value === "object"
        ? JSON.stringify(value, null, 2)
        : String(value);
export const isRecord = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export function validateFilters(filters) {
  if (filters.period === "custom") {
    if (!filters.from || !filters.to)
      return "Choose both a start and end time.";
    if (!dayjs(filters.from).isValid() || !dayjs(filters.to).isValid())
      return "Enter a valid date and time.";
    if (dayjs(filters.from).isAfter(dayjs(filters.to)))
      return "Start time must be before or equal to end time.";
  }
  for (const key of ["action", "actor_email"]) {
    if (filters[key].trim().length > 100)
      return `${FILTER_LABELS[key]} must be 100 characters or fewer.`;
  }
  return "";
}

export function buildSearchQuery(filters, now = dayjs()) {
  const { period, from, to, ...query } = filters;
  Object.keys(FILTER_LABELS).forEach((key) => {
    query[key] = String(query[key] || "").trim();
  });
  if (period === "custom")
    return {
      ...query,
      from: dayjs(from).toISOString(),
      to: dayjs(to).toISOString(),
    };
  if (period === "all") return { ...query, from: "", to: "" };
  const start =
    period === "today"
      ? now.startOf("day")
      : now.subtract(
          period === "7d" ? 7 : 24,
          period === "7d" ? "day" : "hour",
        );
  return { ...query, from: start.toISOString(), to: now.toISOString() };
}

export function sameFilters(a, b) {
  return [...Object.keys(FILTER_LABELS), "period", "from", "to"].every(
    (key) => a[key] === b[key],
  );
}

// Support the formats currently emitted by billing, ownership transfers and IP updates.
export function getChangeRows(changes) {
  if (!isRecord(changes)) return [];
  const has = (object, key) =>
    Object.prototype.hasOwnProperty.call(object, key);
  if (isRecord(changes.before) || isRecord(changes.after)) {
    const before = isRecord(changes.before) ? changes.before : {};
    const after = isRecord(changes.after) ? changes.after : {};
    return [...new Set([...Object.keys(before), ...Object.keys(after)])].map(
      (field) => ({ field, before: before[field], after: after[field] }),
    );
  }
  return Object.entries(changes).map(([field, value]) => {
    if (isRecord(value) && (has(value, "from") || has(value, "to")))
      return { field, before: value.from, after: value.to };
    if (isRecord(value) && (has(value, "before") || has(value, "after")))
      return { field, before: value.before, after: value.after };
    return { field, before: undefined, after: value };
  });
}

export function actorLabel(log) {
  if (log.actor?.name) return log.actor.name;
  if (
    log.details?.actor_type === "system" ||
    log.actor_email === "system@tax-savy"
  )
    return "System";
  if (
    log.details?.actor_type === "anonymous" ||
    log.actor_email === "anonymous@tax-savy"
  )
    return "Anonymous";
  return log.actor_email || log.actor?.email || "Unknown actor";
}

export function logsToCsv(logs) {
  const columns = [
    "id",
    "created_at",
    "actor_email",
    "action",
    "category",
    "target_type",
    "target_id",
    "status",
    "ip_address",
    "request_id",
  ];
  const escape = (value) => {
    let text = String(value ?? "");
    if (/^\s*[=+@-]/.test(text) || /^[\t\r\n]/.test(text)) text = `'${text}`;
    return `"${text.replace(/"/g, '""')}"`;
  };
  return (
    "\uFEFF" +
    [columns, ...logs.map((log) => columns.map((key) => log[key]))]
      .map((row) => row.map(escape).join(","))
      .join("\r\n")
  );
}

export function downloadFile(name, content, type) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const SAVED_VIEWS_KEY = "admin-audit-log-views-v1";
export function readSavedViews() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SAVED_VIEWS_KEY) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (view) =>
          typeof view?.name === "string" &&
          typeof view?.id === "string" &&
          isRecord(view.filters),
      )
      .slice(0, 20)
      .map((view) => {
        const filters = defaultFilters();
        Object.keys(filters).forEach((key) => {
          if (
            typeof filters[key] === "string" &&
            typeof view.filters[key] === "string"
          )
            filters[key] = view.filters[key];
        });
        if (!Object.keys(TIME_PRESETS).includes(filters.period)) filters.period = "24h";
        return { id: view.id, name: view.name, filters };
      });
  } catch {
    return [];
  }
}
